import * as React from 'react';

import { Title } from '../../utils/title';
import SalespersonPerformanceGoalDialog from "./SalespersonPerformanceGoalDialog";
import { useThemeSwitcher } from 'react-css-theme-switcher';
import { useParams, Link } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { fetchApi } from 'services/api';
import { IDName } from 'types/idname';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { TabStrip, TabStripTab, TabStripSelectEventArguments } from '@progress/kendo-react-layout';
import PopupNavBar from 'components/Layout/PopupNavBar';
import { arrowRotateCwIcon, pencilIcon } from '@progress/kendo-svg-icons';
import DashboardTab, { Card, DailyAssignmentSeries, Goal, Granularity } from './DashboardTab';
import CustomersTab from './CustomersTab';
import CompensationTab from './CompensationTab';

type RouteComponentParams = {
  salespersonId: string;
}

type DashboardResponse = {
  Salesperson: IDName;
  Cards: Card[];
  PeriodLabel: string;
  RevenueByDay: number[];
  OrderCountByDay: number[];
  ChartCategories: string[];
  ChartCategoryTitle: string;
  DailyAssignmentSeries: DailyAssignmentSeries[];
  Goal: Goal | null;
  CanViewCompensation: boolean;
}

const SalesReports = () => {

  const { salespersonId: salespersonIdParam } = useParams<RouteComponentParams>();
  const { status } = useThemeSwitcher();
  const [loading, setLoading] = useState(true);
  const [editSalespersonId, setEditSalespersonId] = useState<number | null>(null);
  const [salesperson, setSalesperson] = useState<IDName>({ ID: 0, Name: '' });
  const [cards, setCards] = useState<Card[]>([]);
  const [periodLabel, setPeriodLabel] = useState('');
  const [dailyRevenue, setDailyRevenue] = useState<number[]>([]);
  const [dailyOrderCount, setDailyOrderCount] = useState<number[]>([]);
  const [chartCategories, setChartCategories] = useState<string[]>([]);
  const [chartCategoryTitle, setChartCategoryTitle] = useState('');
  const [dailyAssignmentSeries, setDailyAssignmentSeries] = useState<DailyAssignmentSeries[]>([]);
  const [granularity, setGranularity] = useState(Granularity.Period);
  const [offset, setOffset] = useState(0);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [canViewCompensation, setCanViewCompensation] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);

  // The operational dashboard (cards + chart) is one request; the Customers and Compensation tabs
  // each own a separate, lazily-fetched request (see their components).
  const fetchDashboard = useCallback(() => {
    setLoading(true);
    fetchApi(`/api/Customer/SalespersonDashboard/${salespersonIdParam}`, { Granularity: granularity, Offset: offset }, 'POST')
      .then((response: DashboardResponse) => {
        setSalesperson(response.Salesperson);
        setCards(response.Cards);
        setPeriodLabel(response.PeriodLabel);
        setDailyRevenue(response.RevenueByDay);
        setDailyOrderCount(response.OrderCountByDay);
        setChartCategories(response.ChartCategories ?? []);
        setChartCategoryTitle(response.ChartCategoryTitle);
        setDailyAssignmentSeries(response.DailyAssignmentSeries ?? []);
        setGoal(response.Goal);
        setCanViewCompensation(response.CanViewCompensation);
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Error fetching data');
      })
      .finally(() => setLoading(false));
  }, [salespersonIdParam, granularity, offset]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleTabSelect = (e: TabStripSelectEventArguments) => {
    setSelectedTab(e.selected);
  };

  if (status !== 'loaded') {
    return <div>Loading...</div>;
  }

  return <React.Fragment>
    <PopupNavBar />
    <Title string={`${salesperson.Name} Dashboard`} />
    {editSalespersonId && <SalespersonPerformanceGoalDialog salespersonId={editSalespersonId} close={() => setEditSalespersonId(null)} refresh={fetchDashboard} />}
    <nav className="navbar navbar-dark bg-dark">
      <Link to="/" className="navbar-brand">{salesperson.Name} {loading && <Loader type="pulsing" className="p-0" />}</Link>
       <ul className="nav navbar-nav navbar-right">
        {salespersonIdParam ? <Button
          title="Edit Salesperson"
          icon="edit"
          svgIcon={pencilIcon}
          disabled={loading}
          onClick={() => setEditSalespersonId(parseInt(salespersonIdParam))}
        /> : <Button
          title="Refresh"
          icon="refresh"
          svgIcon={arrowRotateCwIcon}
          disabled={loading}
          onClick={fetchDashboard}
        />}
      </ul>
    </nav>
    <div className="container-fluid">
      <br />
      <TabStrip selected={selectedTab} onSelect={handleTabSelect} animation={false} keepTabsMounted={true}>
        <TabStripTab title="Dashboard">
          <DashboardTab
            cards={cards}
            goal={goal}
            dailyRevenue={dailyRevenue}
            dailyOrderCount={dailyOrderCount}
            dailyAssignmentSeries={dailyAssignmentSeries}
            granularity={granularity}
            offset={offset}
            periodLabel={periodLabel}
            chartCategories={chartCategories}
            chartCategoryTitle={chartCategoryTitle}
            onGranularityChange={(value) => { setGranularity(value); setOffset(0); }}
            onOffsetChange={setOffset}
          />
        </TabStripTab>
        <TabStripTab title="Customers">
          <CustomersTab
            salespersonId={salespersonIdParam}
            salespersonName={salesperson.Name}
            active={selectedTab === 1}
          />
        </TabStripTab>
        {canViewCompensation && <TabStripTab title="Compensation" disabled={!salespersonIdParam}>
          <CompensationTab
            salespersonId={salespersonIdParam}
            active={selectedTab === 2}
          />
        </TabStripTab>}
      </TabStrip>
      <br />
    </div>
  </React.Fragment>
}

export default SalesReports;
